import { createApp } from 'vue'
import { createPinia } from 'pinia'
import { clerkPlugin } from '@clerk/vue'
import App from './App.vue'
import { router } from './router'
import './styles/main.scss'

/**
 * The publishable key is public by design — it identifies the Clerk instance
 * to the browser and is safe to ship in the bundle. The secret key lives only
 * in the Worker.
 *
 * Locally it comes from .env.local (written by `clerk env pull`); in
 * production it is set as a Pages build variable.
 */
const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY

if (!PUBLISHABLE_KEY) {
  throw new Error(
    'Missing VITE_CLERK_PUBLISHABLE_KEY.\n' +
      'Locally:    cd packages/web && clerk env pull\n' +
      'Production: set it as a build variable on the Pages project.',
  )
}

/**
 * Clerk's own UI, dressed in the desert-night palette.
 *
 * These are the same values as the `--bd-*` tokens in styles/main.scss, spelt
 * out rather than read from the cascade because Clerk renders its UI from a
 * CDN bundle that cannot see our stylesheet. Both spellings of the colour
 * variables are supplied — Clerk renamed several between major versions and
 * ignores the ones it does not recognise.
 */
const appearance = {
  variables: {
    colorPrimary: 'hsl(233, 72%, 66%)', // Tuareg indigo
    colorBackground: 'hsl(34, 12%, 6%)', // basalt
    colorText: 'hsl(36, 14%, 93%)', // bone
    colorForeground: 'hsl(36, 14%, 93%)',
    colorTextSecondary: 'hsl(36, 14%, 55%)', // ash
    colorMutedForeground: 'hsl(36, 14%, 55%)',
    colorInputBackground: 'hsl(34, 12%, 9%)', // stone
    colorInput: 'hsl(34, 12%, 9%)',
    colorInputText: 'hsl(36, 14%, 93%)',
    colorInputForeground: 'hsl(36, 14%, 93%)',
    colorNeutral: 'hsl(36, 14%, 93%)',
    colorBorder: 'hsl(34, 12%, 24%)',
    colorDanger: 'hsl(7, 70%, 57%)', // madder
    colorSuccess: 'hsl(152, 33%, 52%)', // verdigris
    colorWarning: 'hsl(39, 78%, 57%)', // saffron
    borderRadius: '2px',
    fontFamily:
      "'Inter', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
  },
}

createApp(App)
  .use(createPinia())
  .use(router)
  .use(clerkPlugin, {
    publishableKey: PUBLISHABLE_KEY,
    appearance,
    /**
     * Keep every redirect on the current origin.
     *
     * Clerk's default `after_sign_in_url` is its own Account Portal
     * (<slug>.accounts.dev/default-redirect), which then forwards to whatever
     * host Clerk has on record for the app -- which was the old pages.dev
     * hostname, now behind a phishing block. Naming relative paths here means
     * sign-in returns to wherever the person actually is, whatever Clerk has
     * stored, and survives the app moving hosts again.
     */
    signInFallbackRedirectUrl: '/',
    signUpFallbackRedirectUrl: '/',
    afterSignOutUrl: '/',
  })
  .mount('#app')
